import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import EditPreAdmissionDetailsForm from './components/EditPersonalInformationForm';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import {
	useDeleteFaceSheetMutation,
	useEditFaceSheetMutation,
	useGetFaceSheetDetailQuery,
} from '@/api/forms-management/faceSheet';
import EditPersonalInformationForm from './components/EditPersonalInformationForm';
import EditContactInformationForm from './components/EditContactInformationForm';
import EditFamilyCarersInformationForm from './components/EditFamilyCarersInformationForm';
import EditEducationWorkLivingStatusForm from './components/EditEducationWorkLivingStatusForm';
import EditMembershipInformationForm from './components/EditMembershipInformationForm';
import EditCaseStatusForm from './components/EditCaseStatusForm';
import EditReferrerInformationForm from './components/EditReferrerInformationForm';
import EditShareCaseServiceForm from './components/EditShareCaseServiceForm';
import EditMentalHealthInformationForm from './components/EditMentalHealthInformationForm';
import EditInitialFaceSheetInformationForm from './components/EditInitialFaceSheetInformationForm';
import EditLatestFaceSheetInformationForm from './components/EditLatestFaceSheetInformationForm';
import { useGetAllEnumListQuery } from '@/api/common';
import { FormManagementStatus } from '@/api/enum/common.enum';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';

export default function EditFaceSheetPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.create);
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const { preAdmissionId, faceSheetId, admissionRequestId } = useParams();
	const [editFaceSheet] = useEditFaceSheetMutation();
	const showModal = useModal();

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: details }: any = useGetFaceSheetDetailQuery(faceSheetId || '', {
		refetchOnMountOrArgChange: true,
	});
	const personalInforRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const contactInforRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const familyCarersInforRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const educationWorkLivingStatusRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const membershipInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const caseStatusRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const referrerInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const shareCaseServiceRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const mentalHealthInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const initialFaceSheetInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const latestFaceSheetInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);

	const handleSubmit = async (type: string) => {
		const results = await Promise.all([
			personalInforRef.current?.validateForm() || Promise.resolve(false),
			contactInforRef.current?.validateForm() || Promise.resolve(false),
			familyCarersInforRef.current?.validateForm() || Promise.resolve(false),
			educationWorkLivingStatusRef.current?.validateForm() ||
				Promise.resolve(false),
			membershipInformationRef.current?.validateForm() ||
				Promise.resolve(false),
			caseStatusRef.current?.validateForm() || Promise.resolve(false),
			referrerInformationRef.current?.validateForm() || Promise.resolve(false),
			shareCaseServiceRef.current?.validateForm() || Promise.resolve(false),
			mentalHealthInformationRef.current?.validateForm() ||
				Promise.resolve(false),
			initialFaceSheetInformationRef.current?.validateForm() ||
				Promise.resolve(false),
			latestFaceSheetInformationRef.current?.validateForm() ||
				Promise.resolve(false),
		]);

		console.log(results);

		const validate = results.some((result) => result === false);
		if (type == 'verify') {
			enqueueSnackbar(
				validate
					? t('snackbar.fieldsVerifiedFalse')
					: t('snackbar.fieldsVerifiedTrue'),
				{
					variant: validate ? 'error' : 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			return;
		}
		if (validate) return;

		const {
			branchId,
			userId,
			nameEn,
			nameZh,
			ageSource,
			dateOfBirth,
			estimatedAge,
			sex,
			hkid,
			dialect,
			maritalStatus,
			placeOfBirth,
			placeOfBirthSpecify,
			yearOfArrivingHk,
		} = {
			...(await personalInforRef.current?.getFormData()),
		};
		const {
			phone,
			homeTel,
			otherPhone,
			typeOfHousing,
			typeOfHousingSpecify,
			addressLanguage,
			region,
			district,
			subdistrict,
			estateCourtVillage,
			roadStreet,
			roadStreetNo,
			buildingBlock,
			buildingBlockSpecify,
			floor,
			flat,
		} = {
			...(await contactInforRef.current?.getFormData()),
		};
		const { familyCarersList } = {
			...(await familyCarersInforRef.current?.getFormData()),
		};
		const {
			financialConditionList,
			workStatus,
			workMotivation,
			education,
			studyingSecondarySchool,
			chiFullNameOfSecondarySchool,
			attendingOtherYouthService,
			nameOfOtherYouthService,
			livingStatus,
			nameOfResidentialCareHome,
			livingWithPersonsAgedUnder18,
			noOfPeopleLivingTogether,
			degreeOfFamilySupport,
		} = {
			...(await educationWorkLivingStatusRef.current?.getFormData()),
		};
		const { mailRequest, newsletterConfidential } = {
			...(await membershipInformationRef.current?.getFormData()),
		};
		const { receivingReferralOrApplicationDate, dateOfConsentObtained } = {
			...(await caseStatusRef.current?.getFormData()),
		};
		const {
			approachSource,
			profileOfReferral,
			profileOfReferralSpecify,
			referralAgency,
			referralAgencySpecify,
			prefixOfReferrer,
			nameOfReferrer,
			positionOfReferrer,
			referrerContactNo,
			referrerFaxNo,
		} = {
			...(await referrerInformationRef.current?.getFormData()),
		};
		const { otherOrganisationList, otherOrganisationServing } = {
			...(await shareCaseServiceRef.current?.getFormData()),
		};
		const {
			followUpClinicPsychiatry,
			mentalHealthConditionList,
			mainDiagnosis,
			mainDiagnosisSpecify,
			secondaryDiagnosis,
			secondaryDiagnosisSpecify,
			suspectedMentalHealthProblem,
			suspectedMentalHealthProblemSpecify,
			suicideIdeation,
			suicideHistory,
			violenceIdeation,
			violenceHistory,
			abuseHistory,
			abuseRole,
			abuseTypeList,
			abuseTypeSpecify,
			addictionProblem,
			typeOfAddictionProblemList,
			typeOfAddictionProblemSpecify,
			coreConcernList,
			dateOfLinkUpToHealthSystem,
			familyMembersHaveHistoryOfMentalIllness,
			otherIllnessDisability,
			enquiryRecordRemarks,
			membershipApplicationRemarks,
			faceSheetRemarks,
		} = {
			...(await mentalHealthInformationRef.current?.getFormData()),
		};
		const { initialFaceSheetWorker, initialFaceSheetDate } = {
			...(await initialFaceSheetInformationRef.current?.getFormData()),
		};
		const { latestFaceSheetWorker, latestFaceSheetDate } = {
			...(await latestFaceSheetInformationRef.current?.getFormData()),
		};

		const formData = {
			branchId,
			userId,
			nameEn,
			nameZh,
			ageSource,
			dateOfBirth,
			estimatedAge,
			sex,
			hkid,
			dialect,
			maritalStatus,
			placeOfBirth,
			placeOfBirthSpecify,
			yearOfArrivingHk,
			phone,
			homeTel,
			otherPhone,
			typeOfHousing,
			typeOfHousingSpecify,
			addressLanguage,
			region,
			district,
			subdistrict,
			estateCourtVillage,
			roadStreet,
			roadStreetNo,
			buildingBlock,
			buildingBlockSpecify,
			floor,
			flat,
			familyCarersList,
			financialConditionList,
			workStatus,
			workMotivation,
			education,
			studyingSecondarySchool,
			chiFullNameOfSecondarySchool,
			attendingOtherYouthService,
			nameOfOtherYouthService,
			livingStatus,
			nameOfResidentialCareHome,
			livingWithPersonsAgedUnder18,
			noOfPeopleLivingTogether,
			degreeOfFamilySupport,
			mailRequest,
			newsletterConfidential,
			approachSource,
			profileOfReferral,
			profileOfReferralSpecify,
			referralAgency,
			referralAgencySpecify,
			prefixOfReferrer,
			nameOfReferrer,
			positionOfReferrer,
			referrerContactNo,
			referrerFaxNo,
			otherOrganisationList,
			otherOrganisationServing,
			followUpClinicPsychiatry,
			mentalHealthConditionList,
			mainDiagnosis,
			mainDiagnosisSpecify,
			secondaryDiagnosis,
			secondaryDiagnosisSpecify,
			suspectedMentalHealthProblem,
			suspectedMentalHealthProblemSpecify,
			suicideIdeation,
			suicideHistory,
			violenceIdeation,
			violenceHistory,
			abuseHistory,
			abuseRole,
			abuseTypeList,
			abuseTypeSpecify,
			addictionProblem,
			typeOfAddictionProblemList,
			typeOfAddictionProblemSpecify,
			coreConcernList,
			dateOfLinkUpToHealthSystem,
			familyMembersHaveHistoryOfMentalIllness,
			otherIllnessDisability,
			enquiryRecordRemarks,
			membershipApplicationRemarks,
			faceSheetRemarks,
			initialFaceSheetWorker,
			initialFaceSheetDate,
			latestFaceSheetWorker,
			latestFaceSheetDate,
			receivingReferralOrApplicationDate,
			dateOfConsentObtained,
			id: details?.data.id || '',
			formStatus:
				type == 'draft'
					? FormManagementStatus.draft
					: FormManagementStatus.submitted,
		};

		if (type == 'submit') {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.submitButtonTitle')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.submitButtonText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					otherButtonText={t('button.saveDraft')}
					otherButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						saveApi({
							...formData,
							formStatus: FormManagementStatus.submitted,
						});
					}}
					onOtherConfirm={async () => {
						saveApi({ ...formData, formStatus: FormManagementStatus.draft });
					}}
				/>
			));
		} else if (type == 'draft') {
			saveApi({ ...formData, formStatus: FormManagementStatus.draft });
		}
	};

	const saveApi = async (formData: any) => {
		const response = await editFaceSheet(formData);
		if ('data' in response) {
			enqueueSnackbar(
				formData.formStatus == FormManagementStatus.submitted
					? t('snackbar.fieldSynchronizationSuccess')
					: t('snackbar.draftEnquiryRecordSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			navigate(
				preAdmissionId
					? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
					: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
			);
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card sx={{ marginTop: 3 }}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography variant='h4' marginBottom={'38px'}>
							{t('drawerListItems.newFaceSheet')}
						</Typography>
						<EditPersonalInformationForm
							ref={personalInforRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditContactInformationForm
							ref={contactInforRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditFamilyCarersInformationForm
							ref={familyCarersInforRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditEducationWorkLivingStatusForm
							ref={educationWorkLivingStatusRef}
							type={type}
							setType={setType}
							details={details?.data}
							allEnumList={allEnumList}
						/>
						<EditMembershipInformationForm
							ref={membershipInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditCaseStatusForm
							ref={caseStatusRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditReferrerInformationForm
							ref={referrerInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditShareCaseServiceForm
							ref={shareCaseServiceRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditMentalHealthInformationForm
							ref={mentalHealthInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
							allEnumList={allEnumList}
						/>
						<EditInitialFaceSheetInformationForm
							ref={initialFaceSheetInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditLatestFaceSheetInformationForm
							ref={latestFaceSheetInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
					</CardContent>

					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button
							type='submit'
							onClick={() =>
								navigate(
									preAdmissionId
										? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
										: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
								)
							}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('draft')}
							endIcon={<EditNoteOutlinedIcon />}
						>
							{t('button.saveDraft')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('verify')}
							endIcon={<TaskOutlinedIcon />}
						>
							{t('button.verifyFields')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('submit')}
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</Box>
		</>
	);
}
